import React, { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "../supabase.config";
import type { User, Session } from "@supabase/supabase-js";
import { toAuthEmail } from "@/lib/loginIdentifier";

interface AuthContext {
  /** identifier: a username (CI, ADB code, ...) or an email */
  login: (identifier: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  user: User | null;
  session: Session | null;
}

const authContext = createContext<AuthContext>({
  login: async () => {},
  logout: async () => {},
  user: null,
  session: null,
})

const useAuth = () => {
  const context = useContext(authContext)
  return context
}

const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  const login = async (identifier: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: toAuthEmail(identifier),
      password,
    });

    if (error) {
      throw error;
    }

    setUser(data.user);
    setSession(data.session);
  }

  const logout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      throw error;
    }
    setUser(null);
    setSession(null);
  }

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [])

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center">Cargando...</div>;
  }

  return (
    <authContext.Provider value={{ login, logout, user, session }}>
      {children}
    </authContext.Provider>
  )
}

// Legacy export for compatibility
const loggedUser = null;

export {
  authContext,
  useAuth,
  AuthProvider,
  loggedUser,
}